import { useEffect, useState } from 'react'
import { gamesApi } from '../lib/api'
import type { GameSession } from '../types'

interface UseRecentSessionsResult {
  sessions: GameSession[]
  loading: boolean
  error: Error | null
}

/**
 * One-shot fetch of the user's most recent game sessions for the
 * dashboard. Runs once on mount; not auto-refreshed. Pages that need a
 * fresh list (e.g. after recording a new game) should re-mount or
 * maintain their own local list — keeping this hook small avoids the
 * cross-cutting concerns of stale-while-revalidate cache invalidation.
 */
export function useRecentSessions(limit = 5): UseRecentSessionsResult {
  const [sessions, setSessions] = useState<GameSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    gamesApi
      .listRecentSessions(limit)
      .then((s) => {
        if (cancelled) return
        setSessions(s)
        setError(null)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err : new Error(String(err)))
        setSessions([])
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [limit])

  return { sessions, loading, error }
}
